/**
 * The itinerary. Reorderable by drag, because the order you click points on a
 * map is rarely the order you want to drive them.
 */

import { useState } from 'react';
import { isPostcodeOnly, type Waypoint } from '@cad/core';

export interface WaypointListProps {
  waypoints: Waypoint[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onToggleKind: (id: string, kind: Waypoint['kind']) => void;
  onRename: (id: string, name: string) => void;
  onReorder: (from: number, to: number) => void;
}

export function WaypointList(props: WaypointListProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  if (props.waypoints.length === 0) {
    return (
      <p className="empty">
        Click the map to drop your first point, then keep clicking to trace the route. Add points
        wherever it needs pushing onto a particular road.
      </p>
    );
  }

  return (
    <ol className="waypoints">
      {props.waypoints.map((waypoint, index) => {
        const isEndpoint = index === 0 || index === props.waypoints.length - 1;
        return (
          <li
            key={waypoint.id}
            className="waypoint"
            draggable
            data-selected={props.selectedId === waypoint.id}
            data-dragover={overIndex === index && dragIndex !== index}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => {
              event.preventDefault();
              setOverIndex(index);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (dragIndex !== null && dragIndex !== index) props.onReorder(dragIndex, index);
              setDragIndex(null);
              setOverIndex(null);
            }}
            onClick={() => props.onSelect(waypoint.id)}
          >
            <span className="waypoint-index" data-kind={waypoint.kind}>
              {index === 0 ? 'A' : index === props.waypoints.length - 1 ? 'B' : index}
            </span>

            <span className="waypoint-text">
              <input
                className="waypoint-name"
                value={waypoint.name ?? ''}
                placeholder={waypoint.kind === 'stop' ? 'Unnamed stop' : 'Shaping point'}
                onChange={(event) => props.onRename(waypoint.id, event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
              {/* A postcode's pin is the middle of the area, not a door. The
                  pins on the map drag, so that is the fix; dragging one
                  renames it after where it lands, and this goes. */}
              {waypoint.kind === 'stop' && isPostcodeOnly(waypoint.name) && (
                <small className="waypoint-hint">Postcode — drag its pin onto the house</small>
              )}
            </span>

            <button
              type="button"
              className="icon-button"
              title={
                isEndpoint
                  ? 'The start and end of a route are always stops'
                  : waypoint.kind === 'stop'
                    ? 'A stop: splits the route and shows in the itinerary'
                    : 'A shaping point: pushes the route onto a road, invisible while driving'
              }
              disabled={isEndpoint}
              onClick={(event) => {
                event.stopPropagation();
                props.onToggleKind(waypoint.id, waypoint.kind === 'stop' ? 'via' : 'stop');
              }}
            >
              {waypoint.kind === 'stop' ? '◉' : '○'}
            </button>

            <button
              type="button"
              className="icon-button danger"
              title="Remove this point"
              onClick={(event) => {
                event.stopPropagation();
                props.onRemove(waypoint.id);
              }}
            >
              ×
            </button>
          </li>
        );
      })}
    </ol>
  );
}
